import React from "react";
import { useParams, Link } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Badge } from "./Projects";
import { Button } from "@/components/ui/button";
import ProjectFiles from "@/components/ProjectFiles";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProjectDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { data: p } = useSWR(`/projects/${id}`, fetcher);
  const { data: takeoffs = [] } = useSWR(`/takeoffs/by-project/${id}`, fetcher);
  const { data: estimates = [] } = useSWR(`/estimates/by-project/${id}`, fetcher);

  if (!p) return <div className="p-10 label-eyebrow text-zinc-500">{t("common.loading")}</div>;

  return (
    <div data-testid="project-detail-page">
      <PageHeader
        eyebrow={`// PROJECT ${p.id}`}
        title={p.name}
        sub={`${p.customer_name} · ${p.address}`}
        action={<Badge status={p.status} />}
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <InfoCard title={t("projects.customer")}>
            <Row k={t("common.name")} v={p.customer_name} />
            <Row k={t("common.phone")} v={p.phone || "—"} />
            <Row k={t("common.email")} v={p.email || "—"} />
            <Row k={t("common.address")} v={p.address} />
          </InfoCard>

          <InfoCard title={lang === "fr" ? "Lead / Suivi" : "Lead / CRM"}>
            <Row k={t("projects.leadSource")} v={(p.lead_source || "—").toUpperCase()} />
            <Row k={t("projects.leadValue")} v={fmt$(p.lead_value || 0)} />
            <Row k={t("projects.followUp")} v={p.next_follow_up || "—"} />
            <Row k={t("common.status")} v={p.status?.replace("_", " ").toUpperCase()} />
          </InfoCard>

          <div className="space-y-6">
            <InfoCard title={t("nav.takeoff")}
              cta={<Link to="/app/takeoff" state={{ project_id: p.id }} className="label-eyebrow text-orange-600">{t("common.new")} →</Link>}>
              {takeoffs.length === 0
                ? <p className="text-sm text-zinc-500 py-1">—</p>
                : takeoffs.slice(0, 3).map(t => <Row key={t.id} k={t.created_at?.slice(0,10)} v={`${t.total_sqft} sqft`} />)}
            </InfoCard>
            <InfoCard title={t("nav.estimator")}
              cta={<Link to="/app/estimator" state={{ project_id: p.id }} className="label-eyebrow text-orange-600">{t("common.new")} →</Link>}>
              {estimates.length === 0
                ? <p className="text-sm text-zinc-500 py-1">—</p>
                : estimates.slice(0, 3).map(e => <Row key={e.id} k={e.created_at?.slice(0,10)} v={fmt$(e.total)} />)}
            </InfoCard>
          </div>
        </div>

        {p.notes && (
          <div className="mt-8 border border-zinc-200 p-5">
            <p className="label-eyebrow text-zinc-500 mb-3">{t("common.notes")}</p>
            <p className="text-sm whitespace-pre-wrap">{p.notes}</p>
          </div>
        )}

        <div className="mt-8">
          <ProjectFiles projectId={p.id} lang={lang} />
        </div>

        <div className="mt-8 flex gap-3 flex-wrap">
          <Link to="/app/takeoff" state={{ project_id: p.id }}>
            <Button data-testid="goto-takeoff" className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase tracking-wider text-xs font-bold">{t("nav.takeoff")}</Button>
          </Link>
          <Link to="/app/estimator" state={{ project_id: p.id }}>
            <Button data-testid="goto-estimator" variant="outline" className="rounded-none border-zinc-300 hover:bg-zinc-900 hover:text-white uppercase tracking-wider text-xs font-bold">{t("nav.estimator")}</Button>
          </Link>
          <Link to="/app/proposals" state={{ project_id: p.id }}>
            <Button data-testid="goto-proposals" variant="outline" className="rounded-none border-zinc-300 hover:bg-zinc-900 hover:text-white uppercase tracking-wider text-xs font-bold">{t("nav.proposals")}</Button>
          </Link>
          <Link to="/app/invoices" state={{ project_id: p.id }}>
            <Button data-testid="goto-invoices" variant="outline" className="rounded-none border-zinc-300 hover:bg-zinc-900 hover:text-white uppercase tracking-wider text-xs font-bold">{t("nav.invoices")}</Button>
          </Link>
        </div>
      </PageBody>
    </div>
  );
}

function InfoCard({ title, children, cta }) {
  return (
    <div className="border border-zinc-200">
      <div className="border-b border-zinc-200 px-5 py-3 flex justify-between items-center">
        <h3 className="font-display font-bold">{title}</h3>{cta}
      </div>
      <div className="p-5 space-y-2">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 text-sm border-b border-zinc-100 pb-2 last:border-b-0">
      <span className="text-zinc-500 mono text-xs">{k}</span>
      <span className="text-right text-zinc-900">{v}</span>
    </div>
  );
}
